'use client'
import React, { FC } from 'react'
import {
  Box,
  Button,
  ButtonProps,
  Heading,
  HStack,
  Icon,
  Link,
  Text,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem
} from '@chakra-ui/react'
import { BsFiles } from 'react-icons/bs'
import CustomModal from '@/app/components/common/CustomModal'
import { MediaDTO, MediaType } from '@/core/media/typings'
import { PiFilePdf } from 'react-icons/pi'
import { Md360, MdDescription, MdVideoFile } from 'react-icons/md'
import {
  gtmEvent,
  RealestateDetailFileClick,
  RealestateDetailFileDownload,
  RealestateDetailInteractiveBrochureRepositoryClick,
  RealestateDetailVideoRepositoryClick
} from '@/utils/gtm'
import { Repository } from '@/core/real-estate/typings'
import { useClientLocale } from '@/hooks/use-locale-config'

interface FilesButtonProps extends ButtonProps {
  medias: MediaDTO[]
  repo?: Repository
  label: string
  description: string
}

const FilesButton: FC<FilesButtonProps> = ({ medias, repo, label, description, ...props }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const pdfFiles = medias.filter((m) => m.type === MediaType.PDF)
  const locale = useClientLocale()

  if (!pdfFiles.length && !repo) {
    return null
  }

  return (
    <CustomModal
      size={{ base: 'full', md: 'lg' }}
      onTriggerClick={() =>
        gtmEvent<RealestateDetailFileClick>({
          name: 'realestate_detail_file_click',
          locale,
          params: {}
        })
      }
      trigger={
        <Button
          minW="auto"
          size="sm"
          bg="background.surface"
          fontWeight="normal"
          rounded="full"
          {...props}>
          <HStack as="span" w="full">
            <Icon as={BsFiles} boxSize={4} />
            <Text as="span" fontSize="md" fontWeight="600" color="text.default">
              Brochures, Flyers y Videos
            </Text>
          </HStack>
        </Button>
      }
      {...{ isOpen, onOpen, onClose }}>
      <VStack spacing={6} p={6} w="full">
        <Heading as="h4" fontSize="2xl">
          {label}
        </Heading>
        <Text>{description}</Text>
        <Wrap gap={2}>
          {pdfFiles.map((file) => (
            <WrapItem
              as={Link}
              display="flex"
              flexDirection="column"
              href={file.url}
              isExternal
              cursor="pointer"
              alignItems="center"
              py={2}
              borderWidth={1}
              rounded="md"
              borderColor="primary"
              onClick={() =>
                gtmEvent<RealestateDetailFileDownload>({
                  name: 'realestate_details_file_download',
                  locale,
                  params: {
                    file_id: file.id
                  }
                })
              }
              key={file.id}
              w="120px">
              <Box m={2} p={2} bg="background.surface" rounded="sm">
                <Icon as={PiFilePdf} boxSize={'80px'} color="primary" />
              </Box>
              <Text align="center" w="full" isTruncated color="gray.400" p={1} fontSize="xs">
                {file.name}
              </Text>
            </WrapItem>
          ))}

          {repo?.video ? (
            <WrapItem
              as={Link}
              display="flex"
              flexDirection="column"
              href={repo.video}
              onClick={() =>
                gtmEvent<RealestateDetailVideoRepositoryClick>({
                  name: 'realestate_detail_video_repository_click',
                  locale,
                  params: {
                    url: repo.video as string
                  }
                })
              }
              isExternal
              cursor="pointer"
              alignItems="center"
              py={2}
              borderWidth={1}
              rounded="md"
              borderColor="primary"
              w="120px">
              <Box m={2} p={2} bg="background.surface" rounded="sm">
                <Icon as={MdVideoFile} boxSize={'80px'} color="primary" />
              </Box>
              <Text align="center" w="full" isTruncated color="gray.400" p={1} fontSize="sm">
                Videos
              </Text>
            </WrapItem>
          ) : null}

          {repo?.virtualTour ? (
            <WrapItem
              as={Link}
              display="flex"
              flexDirection="column"
              href={repo.virtualTour}
              onClick={() =>
                gtmEvent<RealestateDetailVideoRepositoryClick>({
                  name: 'realestate_detail_video_repository_click',
                  locale,
                  params: {
                    url: repo.virtualTour as string
                  }
                })
              }
              isExternal
              cursor="pointer"
              alignItems="center"
              py={2}
              borderWidth={1}
              rounded="md"
              borderColor="primary"
              w="120px">
              <Box m={2} p={2} bg="background.surface" rounded="sm">
                <Icon as={Md360} boxSize={'80px'} color="primary" />
              </Box>
              <Text align="center" w="full" isTruncated color="gray.400" p={1} fontSize="sm">
                Recorrido Virtual
              </Text>
            </WrapItem>
          ) : null}

          {repo?.interactiveBrochure ? (
            <WrapItem
              as={Link}
              display="flex"
              flexDirection="column"
              href={repo.interactiveBrochure}
              onClick={() =>
                gtmEvent<RealestateDetailInteractiveBrochureRepositoryClick>({
                  name: 'realestate_detail_interactive_brochure_repository_click',
                  locale,
                  params: {
                    url: repo.interactiveBrochure as string
                  }
                })
              }
              isExternal
              cursor="pointer"
              alignItems="center"
              py={2}
              borderWidth={1}
              rounded="md"
              borderColor="primary"
              w="120px">
              <Box m={2} p={2} bg="background.surface" rounded="sm">
                <Icon as={MdDescription} boxSize={'80px'} color="primary" />
              </Box>
              <Text align="center" w="full" isTruncated color="gray.400" p={1} fontSize="sm">
                Brochure Interactivo
              </Text>
            </WrapItem>
          ) : null}
        </Wrap>
      </VStack>
    </CustomModal>
  )
}

export default FilesButton
